import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { authService } from '../../services/authService';
import AuthLayout from '../../components/auth/AuthLayout';
import AuthCard from '../../components/auth/AuthCard';
import Button from '../../components/ui/Button';
import logger from '../../utils/logger';

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const genericLoginError = "We couldn’t sign you in. Check your email and password and try again.";

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const requestVersion = useRef(0);

  useLayoutEffect(() => {
    if (typeof location.state?.prefilledEmail === 'string') {
      setEmail(location.state.prefilledEmail);
      setPassword('');
    } else if (location.state?.clearEmail) {
      setEmail('');
      setPassword('');
    }
  }, [location.state]);

  useLayoutEffect(() => {
    requestVersion.current += 1;
    setLoading(false);
    setError('');
    setErrorCode('');
    return () => { requestVersion.current += 1; };
  }, [email, location.key, location.state?.prefilledEmail, location.state?.clearEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const version = ++requestVersion.current;
    setLoading(false);
    const isCurrentRequest = () => version === requestVersion.current;
    setError('');
    setErrorCode('');

    if (!email.trim()) {
      logger.errorEvent('login_invalid_input', { field: 'email' });
      setError(genericLoginError);
      return;
    }

    if (!password) {
      logger.errorEvent('login_invalid_input', { field: 'password' });
      setError(genericLoginError);
      return;
    }

    setLoading(true);

    try {
      const data = await authService.login(email, password);
      if (!isCurrentRequest()) return;

      if (data.requires2FA) {
        navigate('/verify-2fa', { state: { email: email.trim().toLowerCase() } });
      } else {
        login(
          { accessToken: data.accessToken, refreshToken: data.refreshToken },
          data.user,
          data.household,
          data.onboarding,
          data.billing || null,
          data.features || {}
        );

        if (!data.onboarding?.completed) {
          const step = data.onboarding?.currentStep || 1;
          navigate(`/onboarding?step=${step}`);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      if (!isCurrentRequest()) return;
      setError(err?.message || genericLoginError);
      setErrorCode(err?.code || '');
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  const showForgotLink = errorCode === 'LOGIN_INVALID' || errorCode === 'ACCESS_REVOKED';

  return (
    <AuthLayout>
      <AuthCard>
        <div className="auth-heading">
          <h1 className="mt-0 text-[2.25rem] font-bold tracking-[-0.03em] text-[var(--color-text-primary)]">
            Welcome Back
          </h1>
          <p className="mt-2 max-w-[18rem] text-base leading-7 text-[var(--color-text-secondary)]">
            Sign in to continue with Vylo.
          </p>
        </div>

        {error && (
          <div className="auth-error" role="alert">
            <p>{error}</p>
            {showForgotLink && (
              <Link
                to="/forgot-password"
                className="mt-2 inline-block text-sm text-[var(--color-text-secondary)] underline-offset-4 hover:text-[var(--color-text-primary)] hover:underline"
              >
                Forgot your password?
              </Link>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="text-sm font-medium text-[var(--color-text-secondary)]">
              Email
            </label>
            <div className="relative mt-2">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-10 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="text-sm font-medium text-[var(--color-text-secondary)]">
              Password
            </label>
            <div className="relative mt-2">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-10 pr-12 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-1.5 my-auto"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <div className="mt-2 text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-[var(--color-text-secondary)] underline-offset-4 hover:text-[var(--color-text-primary)] hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            loading={loading}
            loadingLabel="Signing in…"
            fullWidth
            className="mt-6"
          >
            Sign in
          </Button>
        </form>

        <div className="auth-footer-divider flex items-center">
          <div className="h-px flex-1 border-t border-[var(--color-border)]" />
          <span className="px-3 text-xs text-[var(--color-text-muted)]">OR</span>
          <div className="h-px flex-1 border-t border-[var(--color-border)]" />
        </div>

        <p className="auth-footer text-[var(--color-text-secondary)]">
          Don&apos;t have an account?{' '}
          <Link
            to="/signup"
            className="font-semibold text-[var(--color-text-primary)] underline-offset-4 hover:underline"
          >
            Sign up
          </Link>
        </p>
      </AuthCard>
    </AuthLayout>
  );
};

export default SignIn;
